import { Helmet } from '@dr.pogodin/react-helmet';
import React from 'react';
import { Link } from 'react-router';
import Loading from '../common/lib/Loading';
import PageNotFound from '../common/lib/PageNotFound';
import type { BrainAtlasType, MultiLang } from '../config';
import Functions from '../functions';
import IndexUtil, { type Index } from './lib/IndexUtil';
import ItemUtil, { type SearchCallbackFunc, type SortCondition } from './lib/ItemUtil';
import XoonipsListIndex from './lib/XoonipsListIndex';
import XoonipsListItem from './lib/XoonipsListItem';

interface Props {
    lang: MultiLang;
    type: BrainAtlasType;
    indexId: number;
}

const XoonipsSearchByIndexId: React.FC<Props> = (props) => {
    const { lang, type, indexId } = props;

    const [notFound, setNotFound] = React.useState<boolean>(false);
    const [index, setIndex] = React.useState<Index | null>(null);
    const [parents, setParents] = React.useState<{ title: string; node: React.ReactNode }>({
        title: '',
        node: null,
    });

    const searchFunc = (condition: SortCondition, func: SearchCallbackFunc) => {
        if (indexId === 0) {
            const res = { total: 0, data: [] };
            func(res);
        } else {
            ItemUtil.getListByIndexId(type, indexId, condition, func);
        }
    };

    React.useEffect(() => {
        IndexUtil.get(type, indexId, (index) => {
            setIndex(index);
            if (index == null) {
                setNotFound(true);
            } else {
                IndexUtil.getParents(type, indexId, (pIndexes) => {
                    const parents = pIndexes.map((index: Index) => {
                        const url: string = IndexUtil.getUrl(type, index.id);
                        const title = Functions.mlang(index.title, lang);
                        return (
                            <React.Fragment key={index.id}>
                                / <Link to={url}>{title}</Link>{' '}
                            </React.Fragment>
                        );
                    });
                    const title = pIndexes
                        .map((index) => {
                            return `/${Functions.mlang(index.title, lang)}`;
                        })
                        .join('');
                    setParents({ title: title, node: parents });
                });
            }
        });
    }, [indexId, lang, type]);

    if (notFound) {
        return <PageNotFound lang={lang} />;
    }

    if (index == null) {
        return <Loading />;
    }
    const baseUrl = indexId === 0 ? '/' : IndexUtil.getUrl(type, indexId);

    return (
        <div className="list">
            <Helmet>
                <title>
                    {Functions.mlang(parents.title, lang)} -{' '}
                    {Functions.mlang('[en]Database[/en][ja]データベース[/ja]', lang)} - {Functions.siteTitle(lang)}
                </title>
            </Helmet>
            <h3>{Functions.mlang('[en]Listing item[/en][ja]アイテム一覧[/ja]', lang)}</h3>
            <div>{parents.node}</div>
            <XoonipsListIndex lang={lang} index={index} type={type} />
            <XoonipsListItem lang={lang} url={baseUrl} search={searchFunc} type={type} />
        </div>
    );
};

export default XoonipsSearchByIndexId;
